import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin, Empty, message, Pagination } from "antd";
import { ArrowLeftOutlined, SyncOutlined } from "@ant-design/icons";
import {
  getOrFetchContent,
  getPlaylistDetail,
  refreshPlaylist,
  PlaylistDetail as PlaylistDetailType,
  DEFAULT_PAGE_SIZE_PLAYLIST_DETAIL,
} from "@sharpana/auth/api_client/summarization_api";
import {
  playlistsContainer,
  emptyContainer,
  darkLoadingContainer,
} from "./my_playlists_styles";
import { playlistHeader, videoList, videoCard } from "./playlist_detail_styles";
import { css } from "@emotion/react";
import * as Sentry from "@sentry/react";

export const FetchAndShowPlaylistDetail = () => {
  const { id } = useParams<{ id: string }>();

  const [playlist, setPlaylist] = useState<PlaylistDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchPlaylist = async () => {
      if (!id) {
        setError("Invalid playlist ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getPlaylistDetail(id, currentPage);
        setPlaylist(data);
      } catch (error) {
        console.error("Failed to fetch playlist:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load playlist";
        setError(errorMessage);
        message.error("Failed to load playlist details");
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaylist();
  }, [id, currentPage]);

  return (
    <div css={playlistsContainer}>
      <PlaylistDetailInner
        id={id}
        playlist={playlist}
        setPlaylist={setPlaylist}
        loading={loading}
        setLoading={setLoading}
        error={error}
        setError={setError}
        autoRefreshOnStart={true}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export const PlaylistDetailInner = ({
  id,
  playlist,
  setPlaylist,
  loading,
  setLoading,
  error,
  setError,
  autoRefreshOnStart = false,
  showBackButton = true,
  pageSize = DEFAULT_PAGE_SIZE_PLAYLIST_DETAIL,
  currentPage = 1,
  setCurrentPage,
}: {
  id: string | undefined;
  playlist: PlaylistDetailType | null;
  setPlaylist: (playlist: PlaylistDetailType | null) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  autoRefreshOnStart: boolean;
  showBackButton: boolean;
  pageSize: number;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
}) => {
  const navigate = useNavigate();

  const [refreshing, setRefreshing] = useState(false);
  const [internalPage, setInternalPage] = useState(currentPage || 1);
  
  // Use the passed setCurrentPage if available, otherwise use internal state
  const updateCurrentPage = useCallback((page: number) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    } else {
      setInternalPage(page);
    }
  }, [setCurrentPage]);

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showDescriptionToggle, setShowDescriptionToggle] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Check if description needs a "show more" toggle
  useEffect(() => {
    if (descriptionRef.current) {
      const { scrollHeight, clientHeight } = descriptionRef.current;
      setShowDescriptionToggle(scrollHeight > clientHeight);
    }
  }, [playlist?.description]);

  const handleRefresh = useCallback(
    async (isAuto = false, page: number = currentPage || internalPage) => {
      if (!id) return;

      try {
        setRefreshing(true);
        // Use the refreshPlaylist function with pagination parameters
        const data = await refreshPlaylist(id, isAuto, page, pageSize);

        if (playlist && page === currentPage) {
          // Merge existing videos with new ones, keeping existing ones if they exist
          const existingVideos = playlist.videos || [];
          const newVideos = data.videos || [];
          const mergedVideos = newVideos.map((newVideo) => {
            const existingVideo = existingVideos.find(
              (v) => v.id === newVideo.id
            );
            return {
              ...existingVideo, // Use existing video as base
              ...newVideo, // Override with new video data
            };
          });
          setPlaylist({ ...data, videos: mergedVideos });
        } else {
          setPlaylist(data);
        }

        // Set current page from the response if available
        if (data.current_page) {
          updateCurrentPage(data.current_page);
        }
      } catch (error) {
        console.error("Failed to refresh playlist:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refresh playlist";
        message.error(errorMessage);
        Sentry.captureException(error);
      } finally {
        setRefreshing(false);
      }
    },
    [id, playlist, setPlaylist, currentPage, internalPage, pageSize, updateCurrentPage]
  );
  
  // Handle page change
  const handlePageChange = useCallback(
    async (page: number) => {
      if (!id) return;
      try {
        setLoading(true);
        // Get the data first before updating the page state
        const data = await getPlaylistDetail(id, page, pageSize);
        setPlaylist(data);
        
        // Update the current page after data is successfully loaded
        if (data.current_page) {
          updateCurrentPage(data.current_page);
        } else {
          updateCurrentPage(page);
        }
        
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Failed to fetch page:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to change page";
        message.error(errorMessage);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    },
    [id, pageSize, setPlaylist, updateCurrentPage]
  );

  // Automatically refresh playlist on mount if the flag is enabled.
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (autoRefreshOnStart && !loading && !autoRefreshedRef.current) {
      autoRefreshedRef.current = true;
      handleRefresh(true); // Pass true to indicate this is an auto-refresh
    }
  }, [autoRefreshOnStart, loading, handleRefresh]);

  async function handleVideoClick(videoUrl: string) {
    setLoading(true);
    setError("");
    const result = await getOrFetchContent(videoUrl);
    if (result.error) {
      setError(result.error);
      setLoading(false);
    } else {
      navigate(`/summary/${result.content_resource_id}?back_enabled=true`);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div css={darkLoadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div>
        <div css={emptyContainer}>
          <Empty description={error || "Playlist not found"} />
          <div style={{ marginTop: "20px" }}>
            {showBackButton && (
              <span
                css={css`
                  color: #1890ff;
                  cursor: pointer;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
                onClick={() => navigate("/")}
              >
                Back to my collection
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Get the effective current page value
  const effectiveCurrentPage = currentPage || internalPage;
  
  // Ensure we have arrays and strings even if the API returns undefined
  const videos = playlist.videos || [];
  const description = playlist.description || "";
  const title = playlist.title || "Untitled Playlist";

  return (
    <div>
      <div css={playlistHeader}>
        {showBackButton && (
          <div className="back-button" onClick={() => navigate("/")}>
            <ArrowLeftOutlined /> Back to my collection
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          {/* Left side: Title and video count */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h1>{title}</h1>
            <div className="meta">
              {playlist.video_count} videos
              {/* • Added on{" "}
              {new Date(playlist.created_at).toLocaleDateString()} */}
            </div>
          </div>
          
          {/* Right side: Sync button and refreshing message */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <SyncOutlined
              spin={refreshing}
              onClick={refreshing ? undefined : () => {
                updateCurrentPage(1); // Always reset to page 1 on manual refresh
                handleRefresh(false, 1); // Pass 1 as the page parameter
              }}
              style={{
                fontSize: "24px",
                marginTop: "6px",
                marginRight: "4px",
                cursor: refreshing ? "not-allowed" : "pointer",
                color: refreshing ? "#bfbfbf" : "#1890ff",
                transition: "all 0.3s",
                opacity: refreshing ? 0.5 : 1,
              }}
            />
            {refreshing && (
              <div style={{ marginTop: "8px" }}>
                <Spin size="small" /> Refreshing playlist...
              </div>
            )}
          </div>
        </div>
        {description && (
          <div className="description-container">
            <p
              ref={descriptionRef}
              className={`description ${
                !isDescriptionExpanded ? "collapsed" : ""
              }`}
            >
              {description}
            </p>
            {showDescriptionToggle && (
              <span
                className="toggle-description"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsDescriptionExpanded(!isDescriptionExpanded);
                  }
                }}
              >
                {isDescriptionExpanded ? "Show less" : "Show more"}
              </span>
            )}
          </div>
        )}
      </div>

      {videos.length === 0 ? (
        <div css={emptyContainer}>
          <Empty description="No videos in this playlist" />
        </div>
      ) : (
        <>
          <div css={videoList}>
            {videos.map((video) => (
              <div
                key={video.id}
                css={videoCard}
                onClick={() => handleVideoClick(video.url)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleVideoClick(video.url);
                  }
                }}
              >
                <div className="thumbnail">
                  {(video.thumbnail ||
                    (video.thumbnails && video.thumbnails.length > 0)) && (
                    <img
                      src={
                        video.thumbnail ||
                        video.thumbnails?.slice(-1)[0]?.url ||
                        "/placeholder-thumbnail.jpg"
                      }
                      alt={`${video.title} thumbnail`}
                      className="loading"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.src = "/placeholder-thumbnail.jpg";
                      }}
                      onLoad={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.classList.remove("loading");
                        img.classList.add("loaded");
                      }}
                    />
                  )}
                </div>
                <div className="info">
                  <h3>{video.title || "Untitled Video"}</h3>
                  {video.description && (
                    <p className="description">{video.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination UI */}
          {playlist.total_pages && playlist.total_pages > 1 && (
            <div css={css`
              display: flex;
              justify-content: center;
              margin: 20px 0;
            `}>
              <Pagination
                current={effectiveCurrentPage}
                total={playlist.total || playlist.video_count || 0}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
